import Dialog from "sap/m/Dialog";
import DatePicker from "sap/m/DatePicker";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import {ICompany, ILoadDataResponse, ISettings, IUser} from "niteroi/packages/system/models/interfaces";
import {WizardStep$CompleteEvent} from "sap/m/WizardStep";
import WizardStep from "sap/m/WizardStep";
import Wizard from "sap/m/Wizard";
import Input from "sap/m/Input";
import Select from "sap/m/Select";
import Logger from "sap/base/Log";
import Default from "niteroi/packages/system/models/Default";
import BaseController from "niteroi/packages/system/controllers/Base.controller";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import {ValueState} from "sap/ui/core/library";

export default class Installer extends BaseController {
    private static readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    private static readonly PHONE_REGEX = /^\+?[0-9()\-.\s]{6,20}$/;

    private installerDialog: Dialog;
    private validationHandlersAttached = false;
    private installerModel: { system: ISettings; company: ICompany; user: IUser } = {
        company: Default.initCompany(),
        user: Default.initUser(),
        system: Default.initSettings()
    };

    public onAfterRendering(): void {
        const model = this.getModel("settings");
        const error = model.getProperty("/error") as string;
        const code = model.getProperty("/code") as number;
        if (error && code) {
            this.showToast(this.getText("installerErrorCodeMessage", [code, error]), 50000);
        } else if (model.getProperty("/systemId") && model.getProperty("/systemHost")) {
            this.showToast(this.getText("installerAlreadyInstalled"), 5000, () => this.navTo("start", {}, false));
        }
    }

    public async onPressInstallSystem(): Promise<void> {
        const view = this.getView();
        // create Dialog
        if (!this.installerDialog) {
            this.installerDialog = await Fragment.load({
                id: view.getId(),
                name: "niteroi.packages.installer.fragments.InstallerDialog",
                controller: this
            }) as Dialog;
        }

        this.installerModel.system.systemId = this.uuidv4();
        this.installerModel.company.companyId = this.uuidv4();
        this.installerModel.user.userId = this.uuidv4();

        let model = this.getModel("installer");
        if (!model) {
            model = new JSONModel(this.installerModel, true);
        }

        this.installerDialog.setModel(model, "installer");

        this.getView().addDependent(this.installerDialog);
        this.installerDialog.open();
        this.attachValidationHandlers();
        this.updateWizardValidationState(false);
    }

    public onActivateLastStep(): void {
    }

    public onNavigationChange(): void {
        this.updateWizardValidationState(true);
    }

    private onFieldValueChange(event: Event): void {
        this.validateControl(event.getSource(), true);
        this.updateWizardValidationState(false);
    }

    private attachValidationHandlers(): void {
        if (this.validationHandlersAttached) {
            return;
        }

        const wizard = this.byId("niteroiInstallNewSystem") as Wizard;
        wizard.getSteps().forEach((step: WizardStep) => {
            this.getStepValidationControls(step).forEach((control: Control) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const managedControl = control as any;
                if (managedControl.isA("sap.m.Input")) {
                    (control as Input).attachLiveChange(this.onFieldValueChange, this);
                } else if (managedControl.isA("sap.m.Select")) {
                    (control as Select).attachChange(this.onFieldValueChange, this);
                } else if (managedControl.isA("sap.m.DatePicker")) {
                    (control as DatePicker).attachChange(this.onFieldValueChange, this);
                }
            });
        });

        this.validationHandlersAttached = true;
    }

    private updateWizardValidationState(setControlState: boolean): boolean {
        const wizard = this.byId("niteroiInstallNewSystem") as Wizard;
        const steps = wizard.getSteps();
        let isWizardValid = true;

        steps.forEach((step: WizardStep, index: number) => {
            if (index === steps.length - 1) {
                return;
            }

            const isStepValid = this.validateStep(step, setControlState);
            step.setValidated(isStepValid);
            isWizardValid = isWizardValid && isStepValid;
        });

        return isWizardValid;
    }

    private validateStep(step: WizardStep, setControlState: boolean): boolean {
        const controls = this.getStepValidationControls(step);
        return controls.every((control: Input | Select | DatePicker) => this.validateControl(control, setControlState));
    }

    private getStepValidationControls(step: WizardStep): Array<Input | Select | DatePicker> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return step.findAggregatedObjects(true, (aggregatedObject: any): boolean => {
            return aggregatedObject.isA("sap.m.Input")
                || aggregatedObject.isA("sap.m.Select")
                || aggregatedObject.isA("sap.m.DatePicker");
        }) as Array<Input | Select | DatePicker>;
    }

    private validateControl(control: Control, setControlState: boolean): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const managedControl = control as any;

        if (managedControl.isA("sap.m.Input")) {
            return this.validateInput(control as Input, setControlState);
        }

        if (managedControl.isA("sap.m.Select")) {
            return this.validateSelect(control as Select, setControlState);
        }

        if (managedControl.isA("sap.m.DatePicker")) {
            return this.validateDatePicker(control as DatePicker, setControlState);
        }

        return true;
    }

    private validateInput(input: Input, setControlState: boolean): boolean {
        const value = input.getValue().trim();
        const inputType = String(input.getType());

        if (input.getRequired() && !value) {
            this.setControlValidationState(input, false, "installerValidationRequired", setControlState);
            return false;
        }

        if (value && inputType === "Email" && !Installer.EMAIL_REGEX.test(value)) {
            this.setControlValidationState(input, false, "installerValidationInvalidEmail", setControlState);
            return false;
        }

        if (value && inputType === "Url" && !this.isValidUrl(value)) {
            this.setControlValidationState(input, false, "installerValidationInvalidUrl", setControlState);
            return false;
        }

        if (value && inputType === "Tel" && !Installer.PHONE_REGEX.test(value)) {
            this.setControlValidationState(input, false, "installerValidationInvalidPhone", setControlState);
            return false;
        }

        this.setControlValidationState(input, true, "", setControlState);
        return true;
    }

    private validateSelect(select: Select, setControlState: boolean): boolean {
        const selectedKey = select.getSelectedKey().trim();
        const isValid = !select.getRequired() || !!selectedKey;
        this.setControlValidationState(select, isValid, "installerValidationRequired", setControlState);
        return isValid;
    }

    private validateDatePicker(datePicker: DatePicker, setControlState: boolean): boolean {
        const value = datePicker.getValue().trim();

        if (datePicker.getRequired() && !value) {
            this.setControlValidationState(datePicker, false, "installerValidationRequired", setControlState);
            return false;
        }

        if (value && !datePicker.getDateValue()) {
            this.setControlValidationState(datePicker, false, "installerValidationInvalidDate", setControlState);
            return false;
        }

        this.setControlValidationState(datePicker, true, "", setControlState);
        return true;
    }

    private setControlValidationState(control: Input | Select | DatePicker, isValid: boolean, messageKey: string, setControlState: boolean): void {
        if (!setControlState) {
            return;
        }

        control.setValueState(isValid ? ValueState.None : ValueState.Error);
        control.setValueStateText(isValid ? "" : this.getText(messageKey));
    }

    private isValidUrl(value: string): boolean {
        try {
            const normalizedValue = /^(https?:\/\/)/i.test(value) ? value : `https://${value}`;
            const parsedUrl = new URL(normalizedValue);
            return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
        } catch {
            return false;
        }
    }

    public onCompleteInstallationWizard(baseEvent: WizardStep$CompleteEvent): void {
        if (!this.updateWizardValidationState(true)) {
            this.showToast(this.getText("installerValidationFillAllRequiredFields"));
            return;
        }

        const wizardStep = baseEvent.getSource().getParent();
        const wizard: Wizard = wizardStep.getParent() as Wizard;
        wizard.setBusy(true);
        const dialog = wizard.getParent() as Dialog;
        const installerModel: JSONModel = dialog.getModel("installer") as JSONModel;
        installerModel.setProperty("/user/companyId", installerModel.getProperty("/company/companyId"));
        installerModel.setProperty("/company/parentId", installerModel.getProperty("/company/companyId"));
        const installerData = installerModel.getData() as { settings: ISettings; company: ICompany; user: IUser };

        Logger.info(`### Works: Installing data ${JSON.stringify(installerData)}`);

        // Send all installation data in one request to /api/settings
        // The backend will handle creating settings, company, and user together
        const installationPayload = {
            ...installerModel.getProperty("/system"),
            company: installerModel.getProperty("/company"),
            user: installerModel.getProperty("/user")
        };

        const requestModel = this.createRequestModel();

        requestModel.loadData("/api/settings", JSON.stringify(installationPayload), true, "POST", false, true, this.getRequestHeaders()).then(() => {
            const response: string = requestModel.getJSON();
            if (!response) {
                Logger.fatal("### Works: Error during system installation", "Installer.onCompleteInstallationWizard");
                throw new Error(`Installation failed`);
            }
            return JSON.parse(response);
        }).then((result: { error: string, settings: ISettings, user: IUser, company: ICompany}) => {
            if (result.error) {
                Logger.fatal("### Works: Installation returned error", result.error, "Installer.onCompleteInstallationWizard");
                throw new Error(result.error);
            }

            // Installation successful - set models from response
            if (result.settings) {
                this.getModel("settings").setData(result.settings);
            }
            if (result.user) {
                const user = result.user;
                if (result.company) {
                    user.company = result.company;
                }
                let userModel = this.getModel("user");
                if (!userModel) {
                    userModel = new JSONModel();
                    this.setModel(userModel, 'user');
                }
                userModel.setData(user);
            }

            Logger.info("### Works: Installation completed successfully", "Installer.onCompleteInstallationWizard");
            wizard.setBusy(false);
            dialog.close();
            this.navTo("start", {}, false);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            wizard.setBusy(false);
            dialog.close();
        });
    }
}
